/**
 * API contract — the single source of truth for the wire shape between client and server.
 * Server routes validate input with these schemas; the client parses output with them.
 * Paths are documented next to each group. All endpoints live under /api and speak JSON,
 * except /api/export/fills.csv.
 */
import { z } from "zod";
import { INDICATOR_KEYS } from "./indicators.js";
import { SECTOR_LABELS } from "./sectors.js";

// ---------- primitives ----------
export const IsoDateTime = z.string().datetime({ offset: true });
export const IsoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "YYYY-MM-DD");
export const Uuid = z.string().uuid();

export const IndicatorKeySchema = z.enum(INDICATOR_KEYS);
export const ConditionOpSchema = z.enum(["<", "<=", ">", ">="]);
export const ConditionWindowSchema = z.enum(["7d", "30d"]);
export const ConditionSchema = z.object({
  key: IndicatorKeySchema,
  window: ConditionWindowSchema.nullable(),
  op: ConditionOpSchema,
  value: z.number().finite(),
  /** 2–30: the indicator series the engine evaluates against holds at most 30 daily points */
  days: z.number().int().min(2).max(30).nullable().optional(),
});

// ---------- auth  (POST /api/auth/login, POST /api/auth/logout, GET /api/auth/me) ----------
export const LoginInput = z.object({ password: z.string().min(1).max(200) });
export const MeOutput = z.object({ authenticated: z.boolean() });

// ---------- tokens  (GET /api/tokens, POST /api/tokens, PATCH /api/tokens/:id, DELETE /api/tokens/:id, GET /api/tokens/:id/candles) ----------
export const WatchStatusSchema = z.enum(["obserwuję", "czekam na poziom", "koszyk aktywny", "wyszedłem"]);
export type WatchStatus = z.infer<typeof WatchStatusSchema>;

export const TokenSchema = z.object({
  id: Uuid,
  cgId: z.string(),
  symbol: z.string(),
  name: z.string(),
  sectors: z.array(z.string()),
  thesis: z.string(),
  priceUsd: z.number().nullable(),
  change24h: z.number().nullable(),
  change7d: z.number().nullable(),
  change30d: z.number().nullable(),
  marketCap: z.number().nullable(),
  fdv: z.number().nullable(),
  ath: z.number().nullable(),
  athDate: IsoDate.nullable(),
  /** CoinGecko rank by market cap */
  marketCapRank: z.number().int().nullable(),
  circulatingSupply: z.number().nullable(),
  totalSupply: z.number().nullable(),
  maxSupply: z.number().nullable(),
  /** total value locked, USD; null when the coin has no TVL data */
  tvlUsd: z.number().nullable(),
  tvlUpdatedAt: IsoDateTime.nullable(),
  /** DefiLlama entity the TVL history comes from */
  tvlSource: z.enum(["protocol", "chain"]).nullable(),
  /** 7-day % change in TVL */
  tvlChange7d: z.number().nullable(),
  /** 30-day % change in TVL */
  tvlChange30d: z.number().nullable(),
  /** last ≤30 daily TVL values, oldest first; empty until synced */
  tvlSparkline: z.array(z.number()),
  /** 24h trading volume, USD, CoinGecko `total_volume` */
  volume24h: z.number().nullable(),
  /** distinct non-anomalous, non-stale exchanges from CoinGecko tickers */
  exchangeCount: z.number().int().nullable(),
  /** display names of top-tier CEXes that list the coin, e.g. ["Binance","Coinbase"] */
  tier1Exchanges: z.array(z.string()),
  tickersUpdatedAt: IsoDateTime.nullable(),
  /** 90-day beta of daily returns vs BTC; null until candles + BTC history exist */
  betaBtc: z.number().nullable(),
  /** 30-day % change of the token/BTC ratio */
  rsBtc30d: z.number().nullable(),
  marketUpdatedAt: IsoDateTime.nullable(),
  /** derived from baskets: koszyk aktywny > czekam na poziom > wyszedłem > obserwuję */
  status: WatchStatusSchema,
  /** most relevant basket (active, else waiting, else latest closed) */
  basketId: Uuid.nullable(),
  /** last ≤30 daily closes, oldest first; empty until candles are synced */
  sparkline: z.array(z.number()),
  createdAt: IsoDateTime,
});
export type Token = z.infer<typeof TokenSchema>;
export const TokenListOutput = z.array(TokenSchema);

export const AddTokenInput = z.object({ cgId: z.string().min(1).max(100) });
export const PatchTokenInput = z.object({
  thesis: z.string().max(4000).optional(),
  sectors: z.array(z.string().min(1).max(40)).max(6).optional(),
});

export const CandleSchema = z.object({
  day: IsoDate,
  open: z.number(),
  high: z.number(),
  low: z.number(),
  close: z.number(),
  /** BTC/USD close for the same day, null when unknown */
  btcClose: z.number().nullable(),
});
export type Candle = z.infer<typeof CandleSchema>;
export const CandlesOutput = z.object({ tokenId: Uuid, candles: z.array(CandleSchema) });

// ---------- market  (GET /api/market/overview, GET /api/market/search?q=, GET /api/market/top) ----------
export const IndicatorReadingSchema = z.object({
  key: IndicatorKeySchema,
  value: z.number().nullable(),
  change7d: z.number().nullable(),
  change30d: z.number().nullable(),
  /** daily values, oldest first, ≤30 points */
  series: z.array(z.number()),
  updatedAt: IsoDateTime.nullable(),
  /** first stored snapshot — history for dominance/TOTAL3 is collected locally */
  historySince: IsoDateTime.nullable(),
});
export type IndicatorReading = z.infer<typeof IndicatorReadingSchema>;

export const RegimeSchema = z.object({
  label: z.string(),
  why: z.string(),
  since: IsoDateTime.nullable(),
  previous: z.object({ label: z.string(), at: IsoDateTime }).nullable(),
});
export type Regime = z.infer<typeof RegimeSchema>;

export const SyncEntrySchema = z.object({
  key: z.string(),
  lastSuccessAt: IsoDateTime.nullable(),
  lastAttemptAt: IsoDateTime.nullable(),
  lastError: z.string().nullable(),
});
export const SyncStatusOutput = z.array(SyncEntrySchema);
export type SyncEntry = z.infer<typeof SyncEntrySchema>;

export const MarketOverviewOutput = z.object({
  indicators: z.array(IndicatorReadingSchema),
  regime: RegimeSchema,
  fx: z.object({ rate: z.number(), date: IsoDate }).nullable(),
  sync: SyncStatusOutput,
});
export type MarketOverview = z.infer<typeof MarketOverviewOutput>;

export const TopCoinSchema = z.object({
  cgId: z.string(),
  symbol: z.string(),
  marketCap: z.number(),
  rank: z.number().int(),
});
export type TopCoin = z.infer<typeof TopCoinSchema>;
/** today's (or latest stored day's) top alts by market cap, ascending rank; empty until the global sync stored market caps */
export const TopCoinsOutput = z.object({ day: IsoDate.nullable(), coins: z.array(TopCoinSchema) });

export const SearchQuery = z.object({ q: z.string().trim().min(1).max(60) });
export const SearchHitSchema = z.object({
  cgId: z.string(),
  symbol: z.string(),
  name: z.string(),
  rank: z.number().nullable(),
  priceUsd: z.number().nullable(),
});
export type SearchHit = z.infer<typeof SearchHitSchema>;
export const SearchOutput = z.object({ own: z.array(TokenSchema), coingecko: z.array(SearchHitSchema) });

// ---------- sectors  (GET /api/sectors, GET /api/sectors/:label) ----------
export const SectorLabelSchema = z.enum([...SECTOR_LABELS]);
export const SeriesPointSchema = z.object({ day: z.string(), value: z.number().nullable() });
export const MemberBriefSchema = z.object({
  cgId: z.string(),
  symbol: z.string(),
  name: z.string(),
  change7d: z.number().nullable(),
  change30d: z.number().nullable(),
  marketCap: z.number().nullable(),
  onWatchlist: z.boolean(),
});
export type MemberBrief = z.infer<typeof MemberBriefSchema>;
export const SectorRowSchema = z.object({
  label: SectorLabelSchema,
  categoryId: z.string(),
  name: z.string(),
  marketCap: z.number().nullable(),
  volume24h: z.number().nullable(),
  volumeToMc: z.number().nullable(),
  change24h: z.number().nullable(),
  return7d: z.number().nullable(),
  return30d: z.number().nullable(),
  rsBtc7d: z.number().nullable(),
  rsBtc30d: z.number().nullable(),
  rsTotal3_7d: z.number().nullable(),
  rsTotal3_30d: z.number().nullable(),
  breadthVsBtc30d: z.number().nullable(),
  breadthPositive7d: z.number().nullable(),
  memberCount: z.number().int(),
  leaders: z.array(MemberBriefSchema),
  laggards: z.array(MemberBriefSchema),
  /** last 30 days of category market cap */
  mcSeries: z.array(SeriesPointSchema),
});
export type SectorRow = z.infer<typeof SectorRowSchema>;
export const EmergingRowSchema = z.object({
  id: z.string(),
  name: z.string(),
  marketCap: z.number(),
  volume24h: z.number().nullable(),
  change24h: z.number().nullable(),
  change7d: z.number().nullable(),
  top3: z.array(z.string()),
});
export type EmergingRow = z.infer<typeof EmergingRowSchema>;
export const ChainSignalSchema = z.enum(["stables_inflow", "tvl_inflow", "outflow"]);
export const ChainRowSchema = z.object({
  chain: z.string(),
  geckoId: z.string().nullable(),
  tokenSymbol: z.string().nullable(),
  tvlUsd: z.number().nullable(),
  stablesUsd: z.number().nullable(),
  tvl7d: z.number().nullable(),
  tvl30d: z.number().nullable(),
  stables7d: z.number().nullable(),
  stables30d: z.number().nullable(),
  stablesToTvl: z.number().nullable(),
  signal: ChainSignalSchema.nullable(),
  /** last 30 days of chain TVL */
  tvlSeries: z.array(SeriesPointSchema),
});
export type ChainRow = z.infer<typeof ChainRowSchema>;
export const ReferenceReturnsSchema = z.object({
  btc7d: z.number().nullable(),
  btc30d: z.number().nullable(),
  total3_7d: z.number().nullable(),
  total3_30d: z.number().nullable(),
});
export const SectorRadarOutput = z.object({
  asOf: z.string().nullable(),
  reference: ReferenceReturnsSchema,
  sectors: z.array(SectorRowSchema),
  emerging: z.array(EmergingRowSchema),
  chains: z.array(ChainRowSchema),
});
export type SectorRadar = z.infer<typeof SectorRadarOutput>;
export const SectorDetailOutput = z.object({
  label: SectorLabelSchema,
  name: z.string(),
  /** all 50 members, ranked */
  members: z.array(MemberBriefSchema),
  /** up to 90 days */
  mcSeries: z.array(SeriesPointSchema),
});
export type SectorDetail = z.infer<typeof SectorDetailOutput>;

// ---------- baskets ----------
// GET /api/baskets, POST /api/baskets, GET /api/baskets/:id, PATCH /api/baskets/:id, DELETE /api/baskets/:id
// POST /api/baskets/:id/start, POST /api/baskets/:id/close
// PUT /api/baskets/:id/buy-fills/:rungIndex, PUT /api/baskets/:id/sell-fills/:tpIndex
export const StartCondSchema = z.enum(["price", "date", "manual", "indicator"]);
export const SpacingSchema = z.enum(["linear", "geometric"]);
export const DistributionSchema = z.enum(["equal", "rising"]);
export const ExitModeSchema = z.enum(["single", "ladder"]);
export const TpTemplateSchema = z.enum(["conservative", "freeride", "allin", "manual"]);
export const BasketLifecycleSchema = z.enum(["waiting", "active", "closed"]);
export const TpLevelSchema = z.object({ m: z.number().gt(1).lte(1000), pct: z.number().min(0).max(100) });

export const BuyFillSchema = z.object({
  rungIndex: z.number().int().min(0).max(11),
  status: z.enum(["filled", "skipped"]),
  price: z.number().positive().nullable(),
  qty: z.number().positive().nullable(),
  filledAt: IsoDateTime.nullable(),
  fxRate: z.number().positive().nullable(),
  fxDate: IsoDate.nullable(),
});
export type BuyFill = z.infer<typeof BuyFillSchema>;

export const SellFillSchema = z.object({
  tpIndex: z.number().int().min(0).max(11),
  price: z.number().positive(),
  qty: z.number().positive(),
  filledAt: IsoDateTime,
  fxRate: z.number().positive().nullable(),
  fxDate: IsoDate.nullable(),
});
export type SellFill = z.infer<typeof SellFillSchema>;

export const CloseSummarySchema = z.object({
  resultUsd: z.number(),
  resultPct: z.number().nullable(),
  days: z.number(),
  buyFilled: z.number(),
  buyTotal: z.number(),
  tpHit: z.array(z.number()),
  tpTotal: z.number(),
  adherencePct: z.number(),
  heldAtClose: z.number(),
  markPrice: z.number().nullable(),
});

const basketEditable = {
  name: z.string().trim().min(1).max(80),
  budgetUsd: z.number().positive().max(1e9),
  startCond: StartCondSchema,
  startX: z.number().positive().nullable(),
  startDate: IsoDate.nullable(),
  startIndicator: ConditionSchema.nullable(),
  startPrice: z.number().positive(),
  floor: z.number().positive(),
  rungCount: z.number().int().min(2).max(12),
  spacing: SpacingSchema,
  dist: DistributionSchema,
  exitMode: ExitModeSchema,
  target: z.number().gt(1).lte(1000),
  tpTemplate: TpTemplateSchema,
  tp: z.array(TpLevelSchema).min(1).max(12),
  moonPct: z.number().min(0).max(50),
  feePct: z.number().min(0).max(5),
  note: z.string().max(4000),
  /** trailing exit — after the first executed TP, alert when price falls this % from its high; null = off */
  trailingPct: z.number().min(1).max(50).nullable(),
  /** indicator condition that, when met, alerts "rozważ realizację pozostałych TP" */
  exitTrigger: ConditionSchema.nullable(),
};

export const BasketSchema = z.object({
  id: Uuid,
  tokenId: Uuid,
  ...basketEditable,
  status: BasketLifecycleSchema,
  startedAt: IsoDateTime.nullable(),
  closedAt: IsoDateTime.nullable(),
  closeSummary: CloseSummarySchema.nullable(),
  buyFills: z.array(BuyFillSchema),
  sellFills: z.array(SellFillSchema),
  /** high-water price since the first executed TP; maintained by the alert engine */
  trailingHigh: z.number().nullable(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type Basket = z.infer<typeof BasketSchema>;
export const BasketListOutput = z.array(BasketSchema);

export const CreateBasketInput = z.object({ tokenId: Uuid });

/** Partial update. Token can be changed only while status = waiting (server enforces). */
export const PatchBasketInput = z
  .object({ ...basketEditable, tokenId: Uuid })
  .partial()
  .refine((p) => p.floor == null || p.startPrice == null || p.floor < p.startPrice, {
    message: "Podłoga musi być niżej niż cena startowa",
    path: ["floor"],
  });
export type PatchBasketInput = z.infer<typeof PatchBasketInput>;

export const StartBasketInput = z.object({
  /** defaults to the current market price on the server */
  startPrice: z.number().positive().optional(),
});

export const CloseBasketInput = z.object({
  /** price used to mark remaining tokens; defaults to current market price */
  markPrice: z.number().positive().optional(),
});

export const PutBuyFillInput = z.discriminatedUnion("status", [
  z.object({ status: z.literal("pending") }),
  z.object({ status: z.literal("skipped") }),
  z.object({
    status: z.literal("filled"),
    price: z.number().positive(),
    qty: z.number().positive(),
    filledAt: IsoDateTime.optional(),
  }),
]);
export type PutBuyFillInput = z.infer<typeof PutBuyFillInput>;

export const PutSellFillInput = z.discriminatedUnion("status", [
  z.object({ status: z.literal("pending") }),
  z.object({
    status: z.literal("filled"),
    price: z.number().positive(),
    qty: z.number().positive(),
    filledAt: IsoDateTime.optional(),
  }),
]);
export type PutSellFillInput = z.infer<typeof PutSellFillInput>;

// ---------- journal  (GET /api/journal?basketId=, POST /api/journal, PATCH /api/journal/:id, DELETE /api/journal/:id) ----------
export const JournalKindSchema = z.enum([
  "manual",
  "fill",
  "sell",
  "start",
  "floor",
  "regime",
  "close",
  "skip",
  "exit",
]);
export const JournalEntrySchema = z.object({
  id: Uuid,
  basketId: Uuid.nullable(),
  kind: JournalKindSchema,
  body: z.string(),
  comment: z.string(),
  createdAt: IsoDateTime,
});
export type JournalEntry = z.infer<typeof JournalEntrySchema>;
export const JournalListOutput = z.array(JournalEntrySchema);
export const JournalQuery = z.object({
  basketId: Uuid.optional(),
  limit: z.coerce.number().int().min(1).max(500).default(200),
});
export const CreateJournalInput = z.object({
  body: z.string().trim().min(1).max(4000),
  basketId: Uuid.nullable().default(null),
});
export const PatchJournalInput = z.object({
  comment: z.string().max(4000).optional(),
  body: z.string().trim().min(1).max(4000).optional(),
});

// ---------- alerts  (GET /api/alerts, POST /api/alerts, PATCH /api/alerts/:id, DELETE /api/alerts/:id, GET /api/alerts/events) ----------
export const AlertKindSchema = z.enum([
  "entry_rung",
  "start_condition",
  "floor",
  "exit_rung",
  "indicator",
  "regime_change",
  "trailing_stop",
  "exit_trigger",
]);
export type AlertKind = z.infer<typeof AlertKindSchema>;

export const AlertSchema = z.object({
  id: Uuid,
  key: z.string(),
  kind: AlertKindSchema,
  basketId: Uuid.nullable(),
  /** 0-based rung / TP index for entry_rung & exit_rung */
  rungIndex: z.number().int().nullable(),
  condition: ConditionSchema.nullable(),
  active: z.boolean(),
  /** false after firing until the trigger clears (re-arm) */
  armed: z.boolean(),
  lastFiredAt: IsoDateTime.nullable(),
  fireCount: z.number().int(),
  createdAt: IsoDateTime,
});
export type Alert = z.infer<typeof AlertSchema>;
export const AlertListOutput = z.array(AlertSchema);
export const CreateAlertInput = z.object({ condition: ConditionSchema });
export const PatchAlertInput = z.object({ active: z.boolean() });

export const AlertEventSchema = z.object({
  id: Uuid,
  alertId: Uuid.nullable(),
  kind: AlertKindSchema,
  message: z.string(),
  channel: z.string(),
  delivered: z.boolean(),
  error: z.string().nullable(),
  firedAt: IsoDateTime,
});
export type AlertEvent = z.infer<typeof AlertEventSchema>;
export const AlertEventsOutput = z.array(AlertEventSchema);

// ---------- settings  (GET /api/settings, PATCH /api/settings) ----------
export const RegimeRuleSchema = z.object({
  label: z.string().min(1).max(40),
  why: z.string().max(300),
  /** all conditions must hold; first matching rule wins */
  all: z.array(ConditionSchema).min(1).max(8),
});
export type RegimeRule = z.infer<typeof RegimeRuleSchema>;

export const ChannelSchema = z.enum(["push", "discord"]);
export const SettingsSchema = z.object({
  plnBeside: z.boolean(),
  dashboardLayout: z.enum(["kafle", "lista"]),
  channel: ChannelSchema,
  /** "blisko szczebla" threshold on the dashboard, percent */
  nearRungPct: z.number().min(0.5).max(30),
  regimeRules: z.array(RegimeRuleSchema).max(12),
  regimeDefaultLabel: z.string().min(1).max(40),
});
export type Settings = z.infer<typeof SettingsSchema>;
export const PatchSettingsInput = SettingsSchema.partial();

export const DEFAULT_SETTINGS: Settings = {
  plnBeside: true,
  dashboardLayout: "kafle",
  channel: "discord",
  nearRungPct: 6,
  regimeDefaultLabel: "Neutralny",
  regimeRules: [
    {
      label: "Altseason",
      why: "ASI > 75 — większość top-50 bije BTC. Czas na TP, nie na nowe koszyki.",
      all: [{ key: "alt_season", window: null, op: ">", value: 75 }],
    },
    {
      label: "Dystrybucja",
      why: "F&G > 75 — euforia. Realizuj plan wyjścia, nie startuj DCA.",
      all: [{ key: "fng", window: null, op: ">", value: 75 }],
    },
    {
      label: "Akumulacja",
      why: "F&G < 30, stablecoiny rosną (7d), BTC.D ≥ 55 %. Alty tanie, ale bez paliwa — moment na start DCA, nie na TP.",
      all: [
        { key: "fng", window: null, op: "<", value: 30 },
        { key: "stable_dominance", window: "7d", op: ">", value: 0 },
        { key: "btc_dominance", window: null, op: ">=", value: 55 },
      ],
    },
    {
      label: "BTC season",
      why: "ASI < 25 — kapitał siedzi w BTC. Alty relatywnie słabe.",
      all: [{ key: "alt_season", window: null, op: "<", value: 25 }],
    },
  ],
};

// ---------- push  (GET /api/push/vapid-public-key, POST /api/push/subscribe) ----------
export const VapidOutput = z.object({ publicKey: z.string().nullable() });
export const PushSubscribeInput = z.object({
  endpoint: z.string().url(),
  keys: z.object({ p256dh: z.string(), auth: z.string() }),
});

// ---------- sync  (GET /api/sync/status, POST /api/sync/now) ----------
export const SyncNowInput = z.object({
  what: z.enum(["prices", "global", "candles", "fx", "cycle", "sectors", "all"]).default("all"),
});

// ---------- errors ----------
export const ErrorCode = z.enum([
  "UNAUTHORIZED",
  "NOT_FOUND",
  "VALIDATION",
  "CONFLICT",
  "UPSTREAM",
  "RATE_LIMITED",
  "INTERNAL",
]);
export type ErrorCode = z.infer<typeof ErrorCode>;
export const ErrorBody = z.object({
  error: ErrorCode,
  message: z.string(),
  requestId: z.string().optional(),
});
export type ErrorBody = z.infer<typeof ErrorBody>;
